import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useFieldOptional } from './field-context';
import { IconCheck, IconChevronDown, IconClose, IconPlus, IconSearch } from './icons';
import { cn, disabledStyles, focusRing } from './utils';

export interface ComboboxItem {
  readonly value: string;
  readonly label: string;
  readonly hint?: string;
  readonly disabled?: boolean;
}

export interface ComboboxProps {
  readonly items: readonly ComboboxItem[];
  readonly value: string | null;
  readonly onValueChange: (value: string | null) => void;
  readonly placeholder?: string;
  readonly emptyMessage?: string;
  readonly disabled?: boolean;
  readonly clearable?: boolean;
  readonly loading?: boolean;
  readonly className?: string;
  readonly ariaLabel?: string;
  /** Busca no servidor: quem usa filtra os `items` e recebe o termo digitado. */
  readonly onSearchChange?: (term: string) => void;
  /**
   * CADASTRO RÁPIDO — o que fazer quando o que se procura ainda não existe.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * POR QUE UM DIÁLOGO, E NUNCA UMA NAVEGAÇÃO
   * ───────────────────────────────────────────────────────────────────────────
   * O pedido é "abrir o cadastro e voltar para onde parou". A forma óbvia —
   * navegar para a tela de departamentos e voltar — perderia tudo o que já foi
   * digitado no formulário de trás, ou obrigaria a persistir rascunho de cada
   * tela em algum lugar. Um diálogo resolve o problema por CONSTRUÇÃO: o
   * formulário de trás continua montado, com o texto onde estava, e a volta é o
   * fechamento do diálogo.
   *
   * `aoConcluir` recebe o identificador do que foi criado, e o combobox o
   * SELECIONA sozinho. Cadastrar um departamento e ainda ter de procurá-lo na
   * lista seria fazer o trabalho pela metade.
   *
   * `termo` é o que a pessoa digitou antes de descobrir que não existia. Vai
   * para o diálogo já preenchido: quem escreveu "Metrologia" e não achou não
   * deve precisar escrever "Metrologia" de novo.
   */
  readonly criar?: {
    /** Some no botão e na linha da lista vazia. Ex.: "Novo departamento". */
    readonly rotulo: string;
    readonly dialogo: (props: {
      readonly termo: string;
      readonly aoConcluir: (value: string) => void;
      readonly aoFechar: () => void;
    }) => ReactNode;
  };
}

/**
 * Combobox (autocomplete de seleção única) implementado sobre Popover.
 *
 * ACESSIBILIDADE — padrão APG "Combobox with List Autocomplete":
 *  · o input tem `role="combobox"`, `aria-expanded`, `aria-controls` e
 *    `aria-activedescendant` apontando para a opção realçada;
 *  · a navegação é por seta ↑/↓, Home/End, Enter para escolher e Esc para
 *    fechar — o foco do DOM NUNCA sai do input, que é o que permite continuar
 *    digitando para refinar;
 *  · a contagem de resultados vai num `aria-live="polite"`, senão quem usa
 *    leitor de tela digita no escuro sem saber se sobrou alguma opção.
 */
export function Combobox({
  items,
  value,
  onValueChange,
  placeholder = 'Buscar…',
  emptyMessage = 'Nenhum resultado encontrado.',
  disabled = false,
  clearable = true,
  loading = false,
  className,
  ariaLabel,
  onSearchChange,
  criar,
}: ComboboxProps) {
  const field = useFieldOptional();
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const statusId = `${baseId}-status`;

  const [open, setOpen] = useState(false);
  const [criando, setCriando] = useState(false);
  /**
   * Impede que UM foco programático reabra a lista.
   *
   * `onFocus` abre o combobox — é o que faz clicar no campo mostrar as opções.
   * Mas depois do cadastro rápido o foco volta ao input por acessibilidade (o
   * diálogo fechou, o foco precisa ir para algum lugar previsível), e esse
   * retorno reabria a lista por cima da escolha que acabara de ser feita: o
   * campo aparecia VAZIO — porque enquanto aberto ele mostra o termo de busca —
   * logo depois de a pessoa cadastrar o departamento. Parecia que não tinha
   * gravado.
   *
   * `ref` e não `state`: precisa valer já na chamada de `focus()`, antes de
   * qualquer nova renderização.
   */
  const ignorarProximoFoco = useRef(false);
  const [term, setTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(() => items.find((i) => i.value === value) ?? null, [items, value]);

  const filtered = useMemo(() => {
    if (onSearchChange) return items; // filtragem é do servidor
    const normalized = normalize(term);
    if (!normalized) return items;
    return items.filter(
      (item) =>
        normalize(item.label).includes(normalized) ||
        (item.hint ? normalize(item.hint).includes(normalized) : false),
    );
  }, [items, term, onSearchChange]);

  const enabled = useMemo(() => filtered.filter((i) => !i.disabled), [filtered]);

  useEffect(() => {
    setActiveIndex(0);
  }, [term, open]);

  // Mantém a opção realçada visível ao navegar por teclado.
  useEffect(() => {
    if (!open) return;
    const active = enabled[activeIndex];
    if (!active) return;
    const node = listRef.current?.querySelector<HTMLElement>(
      `[data-value="${cssEscape(active.value)}"]`,
    );
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, enabled, open]);

  const commit = useCallback(
    (item: ComboboxItem | undefined) => {
      if (!item || item.disabled) return;
      onValueChange(item.value);
      setOpen(false);
      setTerm('');
      inputRef.current?.focus();
    },
    [onValueChange],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) setOpen(true);
        else setActiveIndex((i) => Math.min(i + 1, Math.max(enabled.length - 1, 0)));
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) setOpen(true);
        else setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(Math.max(enabled.length - 1, 0));
        }
        break;
      case 'Enter':
        if (open) {
          event.preventDefault();
          commit(enabled[activeIndex]);
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
          setTerm('');
        }
        break;
      default:
        break;
    }
  };

  /**
   * Abre o cadastro. FECHA a lista antes — duas camadas flutuantes empilhadas
   * competem por foco e por Esc, e o Esc do diálogo acabaria fechando os dois.
   *
   * O termo digitado é preservado: é ele que o diálogo recebe já preenchido.
   */
  const abrirCadastro = useCallback(() => {
    setOpen(false);
    setCriando(true);
  }, []);

  const displayValue = open ? term : (selected?.label ?? '');
  const activeOption = open ? enabled[activeIndex] : undefined;

  /**
   * O CONTAINER INTEIRO CONTA COMO "DENTRO" DO POPOVER.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * O DEFEITO QUE ISTO CORRIGE
   * ───────────────────────────────────────────────────────────────────────────
   * Este componente usa `Popover.Anchor` e NÃO tem `Popover.Trigger`. A
   * diferença não é cosmética: o `DismissableLayer` do Radix trata o TRIGGER
   * como parte do popover e ignora interações com ele. Sem trigger, o input
   * ficava genuinamente "fora" — e o próprio `focusin` que abria o popover era o
   * mesmo que o mandava fechar.
   *
   * A sequência, medida no Chrome contra o beta:
   *
   *   4 ms   `focusin` no input → `setOpen(true)`
   *          React 18 despacha evento de foco de forma SÍNCRONA, então o
   *          `Popover.Content` monta ainda DENTRO do despacho — e registra o
   *          ouvinte de foco-externo antes de o mesmo `focusin` terminar de
   *          borbulhar até o `document`
   *   ~5 ms  o ouvinte recém-registrado vê foco no input, que é "fora", e
   *          dispara `onOpenChange(false)`
   *   175 ms o conteúdo some — a `Presence` do Radix o manteve montado só para
   *          tocar a animação de saída
   *
   * O SINTOMA ERA CRUEL: a lista aparecia com as opções CERTAS e sumia em ~150
   * ms. Quem via jurava que os dados não carregavam, e a suspeita ia para a API
   * — que estava respondendo 200 com tudo certo o tempo todo. Nenhum erro no
   * console, nenhuma requisição falhando.
   *
   * E provava-se contra a intuição: `aria-expanded` já era `false` no primeiro
   * quadro em que a lista estava visível. Ou seja, o popover nunca chegou a
   * ficar aberto; o que se via era a animação de fechamento.
   *
   * A correção é dizer ao Radix o que ele não tem como adivinhar: tudo dentro
   * deste container — input, botão de limpar, ícone — é parte do combobox, e
   * interagir com isso não é "sair".
   */
  const raizRef = useRef<HTMLDivElement>(null);

  const ehInteracaoInterna = useCallback((alvo: EventTarget | null): boolean => {
    return alvo instanceof Node && (raizRef.current?.contains(alvo) ?? false);
  }, []);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <div ref={raizRef} className={cn('relative', className)}>
        <PopoverPrimitive.Anchor asChild>
          <div
            className={cn(
              'flex h-[2.375rem] items-center gap-2 rounded-md border border-border-control bg-surface px-2.5',
              'focus-within:ring-2 focus-within:ring-focus focus-within:ring-offset-2 focus-within:ring-offset-canvas',
              field?.hasError && 'border-danger ring-1 ring-danger',
              disabled && 'cursor-not-allowed opacity-55',
            )}
          >
            <IconSearch size={15} className="shrink-0 text-content-subtle" />
            <input
              ref={inputRef}
              role="combobox"
              type="text"
              autoComplete="off"
              spellCheck={false}
              aria-expanded={open}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={activeOption ? optionId(baseId, activeOption.value) : undefined}
              aria-describedby={joinIds(field?.controlProps['aria-describedby'], statusId)}
              aria-invalid={field?.controlProps['aria-invalid']}
              aria-required={field?.controlProps['aria-required']}
              aria-label={field ? undefined : ariaLabel}
              id={field?.controlId}
              disabled={disabled}
              value={displayValue}
              placeholder={selected ? selected.label : placeholder}
              onChange={(event) => {
                setTerm(event.target.value);
                onSearchChange?.(event.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => {
                if (ignorarProximoFoco.current) {
                  ignorarProximoFoco.current = false;
                  return;
                }
                setOpen(true);
              }}
              onKeyDown={handleKeyDown}
              className={cn(
                'min-w-0 flex-1 bg-transparent text-base text-content outline-none',
                'placeholder:text-content-subtle',
                disabledStyles,
              )}
            />
            {clearable && selected && !disabled ? (
              <button
                type="button"
                onClick={() => {
                  onValueChange(null);
                  setTerm('');
                  inputRef.current?.focus();
                }}
                aria-label={`Limpar seleção: ${selected.label}`}
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded text-content-subtle hover:bg-surface-sunken hover:text-content',
                  focusRing,
                )}
              >
                <IconClose size={14} />
              </button>
            ) : null}
            {criar && !disabled ? (
              <button
                type="button"
                /*
                 * OS DOIS: `onMouseDown` só para não perder o foco, `onClick`
                 * para de fato abrir.
                 *
                 * Só havia `onMouseDown`, e o botão era inalcançável pelo
                 * teclado: Enter e Espaço num `<button>` disparam `click`,
                 * nunca `mousedown`. Quem navega por teclado chegava ao "+",
                 * apertava Enter e não acontecia nada — o cadastro rápido era
                 * mouse-only nos sete pontos em que aparece, incluindo os que
                 * ficam dentro de diálogo.
                 *
                 * O `preventDefault` do mousedown continua sendo o que impede o
                 * clique de roubar o foco do input e fazer o popover piscar
                 * antes de o diálogo abrir; ele NÃO cancela o `click` que vem
                 * em seguida.
                 */
                onMouseDown={(event) => event.preventDefault()}
                onClick={abrirCadastro}
                aria-label={criar.rotulo}
                title={criar.rotulo}
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded',
                  'text-content-subtle hover:bg-brand-subtle hover:text-brand',
                  focusRing,
                )}
              >
                <IconPlus size={15} />
              </button>
            ) : null}
            <IconChevronDown size={15} className="shrink-0 text-content-subtle" />
          </div>
        </PopoverPrimitive.Anchor>

        {/* Contagem de resultados anunciada de forma não intrusiva. */}
        <span id={statusId} aria-live="polite" className="qc-sr-only">
          {loading
            ? 'Carregando opções…'
            : open
              ? `${filtered.length} ${filtered.length === 1 ? 'opção disponível' : 'opções disponíveis'}.`
              : ''}
        </span>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            sideOffset={4}
            align="start"
            /* O foco fica no input: o listbox é apenas apontado por
               aria-activedescendant. Sem isto o Radix roubaria o foco. */
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            /*
             * As três guardas que impedem o combobox de se fechar sozinho.
             * Ver o bloco em `raizRef`, acima: sem `Popover.Trigger`, o Radix
             * considera o próprio input "fora" do popover.
             *
             * `preventDefault()` no evento do Radix CANCELA o descarte — não é
             * o `preventDefault` do DOM. Só cancelamos quando o alvo está de
             * fato dentro deste combobox; clique em qualquer outro lugar da
             * página continua fechando, que é o comportamento certo.
             */
            onFocusOutside={(event) => {
              if (ehInteracaoInterna(event.target)) event.preventDefault();
            }}
            onPointerDownOutside={(event) => {
              if (ehInteracaoInterna(event.target)) event.preventDefault();
            }}
            onInteractOutside={(event) => {
              if (ehInteracaoInterna(event.target)) event.preventDefault();
            }}
            className={cn(
              'z-dropdown w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-md border border-border bg-surface-raised shadow-lg',
              'animate-content-in',
            )}
          >
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel ?? 'Opções'}
              className="max-h-64 overflow-y-auto p-1"
            >
              {loading ? (
                <li className="px-3 py-3 text-sm text-content-subtle">Carregando…</li>
              ) : filtered.length === 0 ? (
                /*
                 * A LISTA VAZIA É A HORA CERTA DE OFERECER O CADASTRO.
                 *
                 * "Nenhum resultado" é um beco: a pessoa digitou um nome que
                 * não existe e a interface só confirma o fracasso. É exatamente
                 * aí que ela sabe o que quer criar, e já escreveu o nome — que
                 * segue para o diálogo preenchido.
                 */
                <li>
                  <p className="px-3 py-3 text-sm text-content-subtle">{emptyMessage}</p>
                  {criar ? (
                    <button
                      type="button"
                      /*
                       * Mesmo par do botão "+" acima: `mousedown` só segura o
                       * foco, `click` age — e `click` é o que Enter e Espaço
                       * disparam.
                       *
                       * DÍVIDA CONHECIDA: esta linha vive dentro do
                       * `Popover.Portal` e o desenho mantém o foco no input de
                       * propósito, então o Tab não chega até aqui — dentro de
                       * um `Dialog` o `FocusScope` nem alcança este container.
                       * Quem navega por teclado usa o "+" ao lado do campo, que
                       * faz a mesma coisa e é alcançável. Trazer ESTA linha para
                       * o teclado exige transformá-la em item da lista
                       * (`role="option"` + `aria-activedescendant`), alcançável
                       * por ↓ — refatoração do modelo de teclado do combobox,
                       * não uma linha.
                       */
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={abrirCadastro}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-3 py-2.5 text-left text-base',
                        'text-brand hover:bg-brand-subtle',
                        focusRing,
                      )}
                    >
                      <IconPlus size={15} className="shrink-0" />
                      <span className="min-w-0 flex-1 truncate">
                        {term.trim() ? `Criar "${term.trim()}"` : criar.rotulo}
                      </span>
                    </button>
                  ) : null}
                </li>
              ) : (
                filtered.map((item) => {
                  const enabledIndex = enabled.indexOf(item);
                  const isActive = enabledIndex >= 0 && enabledIndex === activeIndex;
                  const isSelected = item.value === value;
                  return (
                    <li
                      key={item.value}
                      id={optionId(baseId, item.value)}
                      data-value={item.value}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={item.disabled || undefined}
                      onMouseEnter={() => enabledIndex >= 0 && setActiveIndex(enabledIndex)}
                      onMouseDown={(event) => {
                        event.preventDefault(); // não tirar o foco do input
                        commit(item);
                      }}
                      className={cn(
                        'flex cursor-pointer items-start gap-2 rounded-sm px-2 py-2 text-base',
                        isActive && 'bg-brand-subtle',
                        item.disabled && 'cursor-not-allowed opacity-55',
                      )}
                    >
                      <span className="mt-0.5 w-4 shrink-0">
                        {isSelected ? <IconCheck size={15} className="text-brand" /> : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-content">{item.label}</span>
                        {item.hint ? (
                          <span className="block truncate text-xs text-content-subtle">
                            {item.hint}
                          </span>
                        ) : null}
                      </span>
                    </li>
                  );
                })
              )}
            </ul>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </div>

      {/*
        FORA do `Popover.Portal`, de propósito: dentro dele o diálogo herdaria o
        ciclo de vida do popover — que acabou de fechar — e desmontaria junto,
        antes de aparecer.
      */}
      {/*
        `dialogo()` é chamada no render, mas `aoConcluir` NÃO: quem a invoca é o
        diálogo, depois, em resposta ao clique de quem acabou de cadastrar. A
        regra não consegue provar isso e trata o toque em
        `ignorarProximoFoco.current` como acesso a ref durante o render.
      */}
      {criando && criar
        ? // eslint-disable-next-line react-hooks/refs
          criar.dialogo({
            termo: term.trim(),
            aoConcluir: (novoValor) => {
              /*
               * Seleciona o que acabou de ser criado. Sem isto, a pessoa
               * cadastraria o departamento e teria de procurá-lo na lista —
               * fazer o trabalho pela metade é pior do que não oferecer o atalho.
               */
              onValueChange(novoValor);
              setCriando(false);
              setTerm('');
              // O foco volta ao campo, mas SEM reabrir a lista: quem acabou de
              // cadastrar quer ver o que escolheu, não uma busca em branco.
              ignorarProximoFoco.current = true;
              inputRef.current?.focus();
            },
            aoFechar: () => {
              setCriando(false);
              ignorarProximoFoco.current = true;
              inputRef.current?.focus();
            },
          })
        : null}
    </PopoverPrimitive.Root>
  );
}

function optionId(base: string, value: string): string {
  return `${base}-option-${value.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

function joinIds(...ids: Array<string | undefined>): string | undefined {
  const list = ids.filter((id): id is string => Boolean(id));
  return list.length > 0 ? list.join(' ') : undefined;
}

/** Normaliza para busca: minúsculas e sem acento — usuário não digita acento. */
function normalize(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}
